import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { To } from 'react-router-dom';

export type PageHeaderBack = {
  fallback: To;
  label: ReactNode;
  ariaLabel?: string;
  /** Return `false` to cancel navigation (e.g. unsaved-version prompt). */
  onBeforeNavigate?: () => boolean | void;
};

type BackRegistration = {
  id: symbol;
  back: PageHeaderBack;
};

type TitleRegistration = {
  id: symbol;
  title: string;
};

type PageHeaderNavigationContextValue = {
  mobileBack: PageHeaderBack | undefined;
  mobileTitle: string | undefined;
  registerMobileBack: (id: symbol, back: PageHeaderBack | undefined) => void;
  unregisterMobileBack: (id: symbol) => void;
  registerMobileTitle: (id: symbol, title: string | undefined) => void;
  unregisterMobileTitle: (id: symbol) => void;
};

const PageHeaderNavigationContext = createContext<PageHeaderNavigationContextValue | null>(null);

export function PageHeaderNavigationProvider({ children }: { children: ReactNode }) {
  const [backRegistration, setBackRegistration] = useState<BackRegistration | undefined>();
  const [titleRegistration, setTitleRegistration] = useState<TitleRegistration | undefined>();
  const mobileBack = backRegistration?.back;
  const mobileTitle = titleRegistration?.title;

  const registerMobileBack = useCallback((id: symbol, back: PageHeaderBack | undefined) => {
    setBackRegistration((current) => {
      if (!back) return current?.id === id ? undefined : current;
      return { id, back };
    });
  }, []);

  const unregisterMobileBack = useCallback((id: symbol) => {
    setBackRegistration((current) => (current?.id === id ? undefined : current));
  }, []);

  const registerMobileTitle = useCallback((id: symbol, title: string | undefined) => {
    setTitleRegistration((current) => {
      const next = title?.trim();
      if (!next) return current?.id === id ? undefined : current;
      return { id, title: next };
    });
  }, []);

  const unregisterMobileTitle = useCallback((id: symbol) => {
    setTitleRegistration((current) => (current?.id === id ? undefined : current));
  }, []);

  const value = useMemo(
    (): PageHeaderNavigationContextValue => ({
      mobileBack,
      mobileTitle,
      registerMobileBack,
      unregisterMobileBack,
      registerMobileTitle,
      unregisterMobileTitle,
    }),
    [
      mobileBack,
      mobileTitle,
      registerMobileBack,
      unregisterMobileBack,
      registerMobileTitle,
      unregisterMobileTitle,
    ],
  );

  return (
    <PageHeaderNavigationContext.Provider value={value}>
      {children}
    </PageHeaderNavigationContext.Provider>
  );
}

export function usePageHeaderNavigation() {
  return useContext(PageHeaderNavigationContext);
}

export function useRegisterPageHeaderBack(back: PageHeaderBack | undefined) {
  const context = usePageHeaderNavigation();
  const registrationIdRef = useRef<symbol>(Symbol('page-header-back'));
  const registerMobileBack = context?.registerMobileBack;
  const unregisterMobileBack = context?.unregisterMobileBack;
  const fallback = back?.fallback;
  const label = back?.label;
  const ariaLabel = back?.ariaLabel;
  const onBeforeNavigate = back?.onBeforeNavigate;

  useLayoutEffect(() => {
    if (!registerMobileBack || !unregisterMobileBack) return undefined;

    const registrationId = registrationIdRef.current;
    registerMobileBack(
      registrationId,
      fallback && label ? { fallback, label, ariaLabel, onBeforeNavigate } : undefined,
    );
    return () => {
      unregisterMobileBack(registrationId);
    };
  }, [ariaLabel, fallback, label, onBeforeNavigate, registerMobileBack, unregisterMobileBack]);
}

export function useRegisterPageHeaderTitle(title: string | undefined) {
  const context = usePageHeaderNavigation();
  const registrationIdRef = useRef<symbol>(Symbol('page-header-title'));
  const registerMobileTitle = context?.registerMobileTitle;
  const unregisterMobileTitle = context?.unregisterMobileTitle;

  useLayoutEffect(() => {
    if (!registerMobileTitle || !unregisterMobileTitle) return undefined;

    const registrationId = registrationIdRef.current;
    registerMobileTitle(registrationId, title);
    return () => {
      unregisterMobileTitle(registrationId);
    };
  }, [registerMobileTitle, title, unregisterMobileTitle]);
}

/**
 * Register mobile nav title and/or back for the current page.
 */
export function usePageChrome(chrome: { back?: PageHeaderBack; title?: string } | undefined) {
  useRegisterPageHeaderBack(chrome?.back);
  useRegisterPageHeaderTitle(chrome?.title);
}

/**
 * Use from custom page headers that do not render `AppPageHeader` but still
 * need the mobile app header to expose the same back affordance.
 */
export function usePageChromeBack(back: PageHeaderBack | undefined) {
  useRegisterPageHeaderBack(back);
}

/**
 * Use from custom page headers that do not render `AppPageHeader` but still
 * need the mobile app header to show an inline navigation title.
 */
export function usePageChromeTitle(title: string | undefined) {
  useRegisterPageHeaderTitle(title);
}
